// 用户设置页面 - 现代简约风格
import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Save, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Toast from '../components/Toast';
import LoadingSpinner from '../components/LoadingSpinner';
import LanguageSelector from '../components/LanguageSelector';
import { api } from '../lib/api';

const Settings: React.FC = () => {
    const { t } = useTranslation();

    // 基本信息状态
    const [userName, setUserName] = useState('');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(true);

    // 密码修改状态
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // 在现有状态之后添加
    const [saveLoading, setSaveLoading] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    // Toast状态
    const [toast, setToast] = useState({
        isVisible: false,
        type: 'success' as 'success' | 'error' | 'warning' | 'info',
        title: '',
        message: ''
    });



    // 显示Toast的辅助函数
    const showToast = (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => {
        setToast({ isVisible: true, type, title, message: message || '' });
    };

    const hideToast = () => {
        setToast(prev => ({ ...prev, isVisible: false }));
    };

    // 获取用户信息
    useEffect(() => {
        const fetchUserInfo = async () => {
            try {
                // 使用确实存在的 /api/user/info 接口
                const response = await api.get('/api/user/profile');
        
                if (response.data) {
                    setUserName(response.data.username || 'User');
                    setEmail(response.data.email || 'user@example.com');

                    localStorage.setItem('username', response.data.username || 'User');
                    localStorage.setItem('email', response.data.email || 'user@example.com');
                }
            } catch (error) {
        
                // 如果API失败，使用localStorage中的值
                setUserName(localStorage.getItem('username') || 'User');
                setEmail(localStorage.getItem('email') || 'user@example.com');
            } finally {
                setLoading(false);
            }
        };
        fetchUserInfo();
    }, []);

    // 保存用户名
    const handleSaveBasicInfo = async () => {
        if (!userName.trim()) {
            showToast('error', t('invalidInput'), t('pleaseEnterValidName'));
            return;
        }

        if (userName.trim().length < 2 || userName.trim().length > 30) {
            showToast('error', t('invalidLength'), t('usernameLengthError'));
            return;
        }

        setSaveLoading(true);
        try {
            // 使用新的更新接口
            const response = await api.put('/api/user/profile', {
                username: userName.trim()
            });

            if (response.data) {
                // 更新localStorage
                localStorage.setItem('username', userName.trim());
                showToast('success', t('success'), t('displayNameSaved'));
            }
        } catch (error: any) {
            if (error.response?.status === 400) {
                showToast('error', t('updateFailed'), error.response.data?.error?.message || t('invalidUsername'));
            } else {
                showToast('error', t('updateFailed'), t('failedToUpdateUsername'));
            }
        } finally {
            setSaveLoading(false);
        }
    };

    // 修改密码
    const handleChangePassword = async () => {
        // 验证当前密码
        if (!currentPassword) {
            showToast('error', t('missingField'), t('pleaseEnterCurrentPassword'));
            return;
        }

        // 验证新密码
        if (!newPassword) {
            showToast('error', t('missingField'), t('pleaseEnterNewPassword'));
            return;
        }

        if (newPassword.length < 6) {
            showToast('error', t('invalidPassword'), t('passwordMinLength'));
            return;
        }

        // 验证确认密码
        if (newPassword !== confirmPassword) {
            showToast('error', t('passwordMismatch'), t('newPasswordsDoNotMatch'));
            return;
        }

        setPasswordLoading(true);

        try {
            // 使用正确的API接口
            await api.put('/api/user/password', {
                current_password: currentPassword,
                new_password: newPassword
            });

            showToast('success', t('success'), t('passwordChangedSuccessfully'));

            // 清空密码字段
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            if (error.response?.status === 401) {
                showToast('error', t('authenticationFailed'), t('currentPasswordIncorrect'));
            } else if (error.response?.status === 400) {
                showToast('error', t('updateFailed'), error.response.data?.error?.message || t('invalidPasswordError'));
            } else {
                showToast('error', t('updateFailed'), t('failedToUpdatePassword'));
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    if (loading) {
        return (
            <div>
                <Navbar />
                <LoadingSpinner size="medium" text="Loading user information..." fullPage={true} />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="max-w-2xl mx-auto px-4 py-8">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">{t('accountSettings')}</h1>
                    </div>
                    <p className="text-gray-600 mb-8">{t('manageAccount')}</p>
                </div>

                <div className="space-y-6">
                        {/* 基本信息 */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('basicInformation')}</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('displayName')}
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="text"
                                            value={userName}
                                            onChange={(e) => setUserName(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                            placeholder={t('enterYourName')}
                                        />
                                    </div>
                                </div>

                                {/* 邮箱显示（只读） */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('emailAddress')}
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type="email"
                                            value={email}
                                            readOnly
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 text-sm"
                                        />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{t('contactSupport')}</p>
                                </div>

                                <button
                                    onClick={handleSaveBasicInfo}
                                    disabled={saveLoading}
                                    className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center space-x-2 disabled:opacity-50"
                                >
                                    <Save className="h-4 w-4" />
                                    <span>{saveLoading ? t('saving') : t('saveChanges')}</span>
                                </button>
                            </div>
                        </div>

                        {/* 密码修改 */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('changePassword')}</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('currentPassword')}
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type={showCurrentPassword ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                            placeholder={t('enterCurrentPassword')}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('newPassword')}
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type={showNewPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                            placeholder={t('enterNewPassword')}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('confirmNewPassword')}
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                            placeholder={t('confirmNewPasswordPlaceholder')}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                        </button>
                                    </div>
                                </div>

                                <button
                                    onClick={handleChangePassword}
                                    disabled={passwordLoading}
                                    className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors flex items-center space-x-2 disabled:opacity-50"
                                >
                                    <Lock className="h-4 w-4" />
                                    <span>{passwordLoading ? t('updating') : t('updatePassword')}</span>
                                </button>
                            </div>
                        </div>
                </div>
            </div>
            
            {/* Toast通知 */}
            <Toast
                type={toast.type}
                title={toast.title}
                message={toast.message}
                isVisible={toast.isVisible}
                onClose={hideToast}
            />
        </div>
    );
};

export default Settings;